import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoRelatorio, { useMarcaDaguaRelatorio } from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";
import "./RelatorioLocacaoEquipamentosPrint.css";

function formatarDataBR(data) {
  if (!data || !String(data).includes("-")) return data || "-";
  const [ano, mes, dia] = String(data).split("-");
  return `${dia}/${mes}/${ano}`;
}

function formatarPeriodo(dataInicio, dataFim) {
  const inicio = formatarDataBR(dataInicio);
  const possuiDataFinal = /^\d{4}-\d{2}-\d{2}$/.test(String(dataFim || ""));
  return possuiDataFinal ? `${inicio} a ${formatarDataBR(dataFim)}` : `${inicio} — Em andamento`;
}

function imprimir() {
  window.print();
}

export default function RelatorioLocacaoEquipamentosPrint({ locacao, onVoltar }) {
  const marcaDaguaUrl = useMarcaDaguaRelatorio();
  useNomeArquivoPdf(locacao.codigo || "Locacao_Equipamentos");

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "print-style-locacao-equipamentos";
    style.media = "print";
    style.textContent = "@page { size: A4 portrait; margin: 0; }";
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  return (
    <Box className="locacao-relatorio-root">
      <Box className="locacao-print-actions">
        <button onClick={onVoltar}>Voltar</button>
        <button onClick={imprimir}>Imprimir</button>
      </Box>

      <main
        className="locacao-relatorio-pagina"
        style={{ "--marca-dagua-locacao": `url("${marcaDaguaUrl}")` }}
      >
        <header className="locacao-cabecalho">
          <div className="locacao-logo">
            <div className="locacao-logo-fundo">
                <LogoRelatorio alt="Logo" variante="calibracao-prensa" />
            </div>
          </div>
          <div className="locacao-titulo">
            <strong>TERMO DE LOCAÇÃO DE EQUIPAMENTOS</strong>
            <span>Sistema de Gestão da Qualidade</span>
          </div>
          <div className="locacao-codigo">
            <strong>{locacao.codigo || "-"}</strong>
            <span>{formatarDataBR(locacao.dataInicio)}</span>
          </div>
        </header>

        <section className="locacao-resumo">
          <div className="locacao-resumo-card">
            <span className="locacao-resumo-icone"><PersonOutlineIcon /></span>
            <div><strong>Cliente</strong><span>{locacao.clienteNome || "-"}</span></div>
          </div>
          <div className="locacao-resumo-card">
            <span className="locacao-resumo-icone"><BusinessOutlinedIcon /></span>
            <div><strong>Obra</strong><span>{locacao.obraNome || "-"}</span></div>
          </div>
          <div className="locacao-resumo-card">
            <span className="locacao-resumo-icone"><CalendarTodayOutlinedIcon /></span>
            <div>
              <strong>Período</strong>
              <span>{formatarPeriodo(locacao.dataInicio, locacao.dataFim)}</span>
            </div>
          </div>
          <div className="locacao-resumo-card locacao-resumo-status">
            <span className="locacao-resumo-icone"><CheckCircleOutlineIcon /></span>
            <div><strong>Status</strong><span className="locacao-status-selo">{locacao.status || "-"}</span></div>
          </div>
        </section>

        <div className="locacao-secao-titulo"><h2>Equipamentos locados</h2><span /></div>
        <table className="locacao-itens">
          <thead>
            <tr>
              <th className="locacao-numero">Nº</th>
              <th className="locacao-item">Item da locação</th>
              <th>Equipamentos</th>
              <th>Quantidade</th>
              <th>Tipo</th>
              <th>Acessórios / partes do kit</th>
            </tr>
          </thead>
          <tbody>
            {(locacao.itens || []).map((item, indice) => (
              <React.Fragment key={item.id || indice}>
                <tr>
                  <td>{indice + 1}</td>
                  <td><strong>{item.nomeLocacao || "-"}</strong></td>
                  <td>
                    {(item.equipamentos || []).map((equipamento) => (
                      <div key={equipamento.id}>
                        {[equipamento.nome, equipamento.descricao].filter(Boolean).join(" — ")}
                      </div>
                    ))}
                  </td>
                  <td>{item.quantidade ?? "-"}</td>
                  <td>{item.unidade || "-"}</td>
                  <td>{item.acessorios || "-"}</td>
                </tr>
                {item.observacao && (
                  <tr className="locacao-item-observacao">
                    <td colSpan="6"><strong>Observação do item:</strong> {item.observacao}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <section className="locacao-retirada">
          <div className="locacao-secao-titulo"><h2>Responsável pela retirada</h2><span /></div>
          <table>
            <tbody>
              <tr><th>Nome</th><td>{locacao.responsavelRetirada || "-"}</td></tr>
              <tr><th>CPF / Documento</th><td>{locacao.documentoResponsavelRetirada || "-"}</td></tr>
              <tr><th>Telefone</th><td>{locacao.telefoneResponsavelRetirada || "-"}</td></tr>
            </tbody>
          </table>
        </section>

        <section className="locacao-observacoes">
          <div className="locacao-secao-titulo"><h2>Observações</h2><span /></div>
          <p>{locacao.observacoes || "-"}</p>
        </section>

        <p className="locacao-declaracao">
          Declaro que recebi os equipamentos relacionados neste termo e estou ciente das condições de uso, conservação e devolução.
        </p>

        <section className="locacao-assinaturas">
          <div>
            <span className="locacao-linha-assinatura" />
            <strong>Assinatura do cliente</strong>
            <small>{locacao.clienteAssinaturaNome || locacao.clienteNome || ""}</small>
            {locacao.clienteAssinaturaCargo && <small>{locacao.clienteAssinaturaCargo}</small>}
          </div>
          <div>
            <span className="locacao-linha-assinatura" />
            <strong>Responsável pela retirada</strong>
            <small>{locacao.responsavelRetirada || ""}</small>
            {locacao.documentoResponsavelRetirada && <small>{locacao.documentoResponsavelRetirada}</small>}
          </div>
        </section>
      </main>
    </Box>
  );
}
